'use client'

import { useCallback, useState } from 'react'
import { cartTotalToRazorpayPaise } from '@/src/lib/razorpay/amount'
import {
  loadRazorpayScript,
  openRazorpayCheckout,
  type RazorpayPaymentSuccess,
} from '@/src/lib/razorpay/client'

export type RazorpayCheckoutOptions = {
  /** Called after the payment signature is verified server-side. */
  onVerified?: (response: RazorpayPaymentSuccess) => void | Promise<void>
}

const CHECKOUT_NAME = 'VALLOTÉ'
const CHECKOUT_DESCRIPTION = 'Order payment'
const THEME_COLOR = '#1a1a1a'

export function useRazorpayCheckout() {
  const [isProcessing, setIsProcessing] = useState(false)

  const startCheckout = useCallback(async (
    totalAmount: number | undefined,
    options?: RazorpayCheckoutOptions,
  ) => {
    const keyId = process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID
    if (!keyId) {
      window.alert('Razorpay is not configured (missing NEXT_PUBLIC_RAZORPAY_KEY_ID).')
      return
    }

    const amountPaise = cartTotalToRazorpayPaise(totalAmount)
    setIsProcessing(true)
    try {
      const createRes = await fetch('/api/payments/razorpay/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amountPaise }),
      })
      const createJson = await createRes.json().catch(() => ({}))
      if (!createRes.ok) {
        window.alert(createJson?.error ?? 'Could not start payment. Try again.')
        return
      }

      const orderId = createJson.orderId as string
      const orderAmount = Number(createJson.amount)

      const loaded = await loadRazorpayScript()
      if (!loaded) {
        window.alert('Could not load Razorpay checkout.')
        return
      }

      const opened = openRazorpayCheckout({
        key: keyId,
        amount: orderAmount,
        currency: String(createJson.currency ?? 'INR'),
        order_id: orderId,
        name: CHECKOUT_NAME,
        description: CHECKOUT_DESCRIPTION,
        theme: { color: THEME_COLOR },
        handler: async (response: RazorpayPaymentSuccess) => {
          const verifyRes = await fetch('/api/payments/razorpay/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(response),
          })
          const verifyJson = await verifyRes.json().catch(() => ({}))
          if (verifyJson.valid) {
            if (options?.onVerified) {
              await options.onVerified(response)
            } else {
              window.alert(`Payment successful. Payment id: ${response.razorpay_payment_id}`)
            }
          } else {
            window.alert('Payment could not be verified. Contact support with your order details.')
          }
        },
      })

      if (!opened) {
        window.alert('Could not open Razorpay checkout.')
      }
    } catch {
      window.alert('Something went wrong. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }, [])

  return { isProcessing, startCheckout }
}
